import React from 'react'
import type { GetStaticPaths, GetStaticProps } from 'next'

import { isSlugAPageNumber } from '@marketing-sites/utils'
import type { ArticleIndexPageProps } from '../ArticleIndexPage'
import {
  ArticlesIndexPage,
  getStaticPathsForArticleIndexPage,
  getStaticPropsForArticleIndexPage,
} from '../ArticleIndexPage'

import type { ArticlePageProps } from '../ArticlePage'
import {
  ArticlePage,
  getStaticPathsForArticlePage,
  getStaticPropsForArticlePage,
} from '../ArticlePage'

export type ArticlesIndexPageOrArticlePageProps =
  | ArticleIndexPageProps
  | ArticlePageProps

export function ArticlesIndexPageOrArticlePage(
  props: ArticlesIndexPageOrArticlePageProps,
) {
  if (props.kind === 'ArticleIndexPageProps') {
    return <ArticlesIndexPage {...props} />
  }
  return <ArticlePage {...props} />
}

export const getStaticPathsForArticleIndexPagesAndArticlePages: GetStaticPaths =
  async (context) => {
    const articleIndexPagePaths =
      await getStaticPathsForArticleIndexPage(context)
    const articlePagePaths = await getStaticPathsForArticlePage(context)

    const result = {
      paths: [...articleIndexPagePaths.paths, ...articlePagePaths.paths],
      //fallback: 'blocking' allows for new pages to be created without rebuilding the application
      fallback: 'blocking' as const,
    }
    return result
  }

export const getStaticPropsForArticleIndexPageOrArticlePage: GetStaticProps<
  ArticlesIndexPageOrArticlePageProps
> = async (context) => {
  const { params } = context

  const result = isSlugAPageNumber(params?.['slug'])
    ? await getStaticPropsForArticleIndexPage(context)
    : await getStaticPropsForArticlePage(context)

  // show a not found page if the page number or article is not found
  if ('props' in result) {
    const articleItems =
      result.props.data?.composePageArticleCollection?.items ?? []
    if (articleItems.length === 0) {
      if (process.env['NEXT_PHASE'] == 'phase-production-build') {
        // fix for -> Error: `redirect` can not be returned from getStaticProps during prerendering
        return {
          notFound: true,
        }
      } else {
        return {
          redirect: {
            destination: '/not-found/',
            permanent: false,
          },
        }
      }
    }
  }
  // show the article page if the article is found
  return result
}
